import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useAppSelector } from '../hooks/redux';
import { isPromptSuppressedPath } from '../utils/promptSuppression';
import { brand } from '../branding';

// The one in-app ask to turn notifications on. A line at the top of the page,
// in the flow, never over anything — it must not stop anyone doing their job.
//
// Rules (owner, 2026-09-06):
//   - everyone sees it the first time they sign in with notifications off
//   - "Remind me later" hides it and it comes back after a week
//   - "Don't ask again" hides it for good on this device
//   - it never shows once notifications are on, or once the browser has
//     blocked them (a blocked permission can only be undone in OS settings,
//     so asking again is noise)
//   - never on screens whose bottom action must not be crowded (the same
//     suppression list the install / update cards use)
//
// This replaces the floating bottom-right card that used to appear after
// sign-in: that one was easy to dismiss reflexively and then silent for a
// month, and a 27% opt-in rate in prod said so.
export const REMIND_LATER_KEY = 'sproutly.notificationBanner.remindAt';
export const NEVER_ASK_KEY = 'sproutly.notificationBanner.never';
export const REMIND_LATER_MS = 7 * 24 * 60 * 60 * 1000;

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode — we'll simply ask again next session */
  }
};

// In-memory mirror of the two choices, so they hold for the rest of the
// tab even where localStorage writes throw (private mode, blocked site
// data) — "Don't ask again" is a promise to the user, not a best effort.
let sessionNever = false;
let sessionRemindAt: number | null = null;

/** Test hook: forget the in-memory choices (storage is the caller's to clear). */
export const resetBannerMemory = () => {
  sessionNever = false;
  sessionRemindAt = null;
};

/** True while a "Remind me later" is still within its week, or "Don't ask again" was chosen. */
export const isSnoozed = (now = Date.now()): boolean => {
  if (sessionNever || readStorage(NEVER_ASK_KEY) === '1') return true;
  // Storage is the record; the in-memory stamp only stands in when nothing
  // could be written (so a later, deliberate change to storage still wins).
  const raw = readStorage(REMIND_LATER_KEY);
  const at = raw ? Number.parseInt(raw, 10) : sessionRemindAt ?? NaN;
  if (!Number.isFinite(at)) return false;
  // A stamp in the future (a tablet whose clock was wrong when it was
  // written) must not hide the banner for years: treat it as expired.
  const elapsed = now - at;
  return elapsed >= 0 && elapsed < REMIND_LATER_MS;
};

const NotificationBanner: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { isSupported, isIosSafari, isStandalone, permission, subscribe } = usePushNotifications();
  const [hidden, setHidden] = useState(() => isSnoozed());
  const [busy, setBusy] = useState(false);

  // On iOS, push only works from a Home Screen app; explain that instead of
  // offering a button that cannot succeed.
  const iosNeedsInstall = isIosSafari && !isStandalone;
  const canAsk = isSupported || iosNeedsInstall;

  if (!isAuthenticated || hidden || !canAsk || permission !== 'default') return null;
  if (isPromptSuppressedPath(location.pathname)) return null;

  const remindLater = () => {
    sessionRemindAt = Date.now();
    writeStorage(REMIND_LATER_KEY, String(sessionRemindAt));
    setHidden(true);
  };
  const neverAsk = () => {
    sessionNever = true;
    writeStorage(NEVER_ASK_KEY, '1');
    setHidden(true);
  };
  const enable = async () => {
    setBusy(true);
    try {
      await subscribe();
    } finally {
      setBusy(false);
      // Whatever they answered in the OS dialog, `permission` is no longer
      // 'default' — the banner leaves on its own on the next render.
      setHidden(true);
    }
  };

  return (
    <div
      data-testid="notification-banner"
      role="region"
      aria-label="Turn on notifications"
      className="border-b border-primary-100 bg-primary-50 px-4 py-2.5 sm:px-6"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2">
        <BellIcon className="h-5 w-5 flex-shrink-0 text-primary-600" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-gray-800">
          {iosNeedsInstall ? (
            <>
              To get notifications on this iPhone or iPad, add {brand.name} to your Home Screen
              first: tap Share, then Add to Home Screen.
            </>
          ) : (
            <>
              Turn on notifications to hear about messages, incidents and reminders even when{' '}
              {brand.name} is closed.
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          {!iosNeedsInstall && (
            <button
              type="button"
              data-testid="notification-banner-enable"
              disabled={busy}
              onClick={enable}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Turning on…' : 'Turn on'}
            </button>
          )}
          <button
            type="button"
            data-testid="notification-banner-later"
            onClick={remindLater}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-primary-100"
          >
            Remind me later
          </button>
          <button
            type="button"
            data-testid="notification-banner-never"
            onClick={neverAsk}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-primary-100"
          >
            Don&apos;t ask again
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;
